import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination,
  Select,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";

import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { exportBalancePaymentsToPDF } from "../../utils/exportBalancePayments";

const ALL_SHOPS_OPTION = { id: "", name: "All Shops" };

export default function BalancePaymentTable() {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedShop, setSelectedShop] = useState("");
  const [shopSearchTerm, setShopSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const queryString = useMemo(() => {
    const params = ["page_size=all"];
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    if (selectedCustomer) params.push(`customer=${selectedCustomer}`);
    if (selectedShop) params.push(`shop=${selectedShop}`);
    return params.join("&");
  }, [startDate, endDate, selectedCustomer, selectedShop]);

  const {
    data: balancePaymentsData,
    isLoading,
    isError,
  } = useFetchObjects(
    ["customer-balance-payments-report", queryString],
    `shop/customer-balance-payments/?${queryString}`,
    token
  );

  const customerFetchUrl = useMemo(() => {
    let url = "shop/customers?page_size=all";
    if (selectedShop) {
      url += `&shop=${selectedShop}`;
    }
    return url;
  }, [selectedShop]);

  const { data: customers } = useFetchObjects(
    ["customers", selectedShop],
    customerFetchUrl,
    token
  );

  const { data: shopsData, isLoading: shopsLoading } = useFetchObjects(
    ["shops", "balance-payment-report-all-filter", shopSearchTerm],
    `shop/shop/?search=${encodeURIComponent(shopSearchTerm.trim())}&page_size=20`,
    token
  );

  const payments = useMemo(
    () => balancePaymentsData?.results || balancePaymentsData || [],
    [balancePaymentsData]
  );
  const customersList = useMemo(
    () => customers?.results || customers || [],
    [customers]
  );
  const customerOptions = useMemo(
    () => [{ id: "", name: t("reports.all_customers") }, ...customersList],
    [customersList, t]
  );
  const selectedCustomerOption = useMemo(
    () =>
      customerOptions.find(
        (customer) => String(customer.id) === String(selectedCustomer)
      ) || customerOptions[0],
    [customerOptions, selectedCustomer]
  );

  const baseShopOptions = useMemo(
    () => (Array.isArray(shopsData) ? shopsData : shopsData?.results || []),
    [shopsData]
  );
  const shopOptions = useMemo(
    () => [ALL_SHOPS_OPTION, ...baseShopOptions],
    [baseShopOptions]
  );
  const selectedShopOption = useMemo(
    () =>
      shopOptions.find((shop) => String(shop.id) === String(selectedShop)) ||
      ALL_SHOPS_OPTION,
    [shopOptions, selectedShop]
  );

  // Total customer opening balance (existing dues) per currency
  const totalOpeningBalance = useMemo(() => {
    const byCurrency = {};
    customersList.forEach((c) => {
      const cur = c.opening_balance_currency_details?.title || "AF";
      const amt = parseFloat(c.opening_balance) || 0;
      if (amt !== 0) byCurrency[cur] = (byCurrency[cur] || 0) + amt;
    });
    return byCurrency;
  }, [customersList]);

  const filteredPayments = useMemo(() => {
    return payments.filter((payment) => {
      const paymentDate = payment.payment_date ? dayjs(payment.payment_date) : null;

      if (
        selectedCustomer &&
        String(payment.customer_details?.id ?? payment.customer) !==
          String(selectedCustomer)
      ) {
        return false;
      }

      if (startDate && paymentDate && paymentDate.isBefore(startDate.startOf("day"))) {
        return false;
      }

      if (endDate && paymentDate && paymentDate.isAfter(endDate.endOf("day"))) {
        return false;
      }

      if ((startDate || endDate) && !paymentDate) {
        return false;
      }

      return true;
    });
  }, [payments, selectedCustomer, startDate, endDate]);

  const totalCount = filteredPayments.length;

  const displayPayments = useMemo(() => {
    if (rowsPerPage === -1) return filteredPayments;
    const startIndex = (page - 1) * rowsPerPage;
    return filteredPayments.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredPayments, page, rowsPerPage]);

  const totalAmount = useMemo(
    () =>
      filteredPayments.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0),
    [filteredPayments]
  );

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `collected_payments_${today}`;
    if (type === "pdf") {
      exportBalancePaymentsToPDF(filteredPayments, `${base}.pdf`);
    }
    handleClose();
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="error">
          {t("reports.error_fetching_data")}{" "}
          <RefreshIcon sx={{ cursor: "pointer", verticalAlign: "middle" }} />
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <DatePicker
                  label={t("reports.datepicker_start")}
                  value={startDate}
                  onChange={(value) => {
                    setStartDate(value);
                    setPage(1);
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t("reports.datepicker_end")}
                  value={endDate}
                  onChange={(value) => {
                    setEndDate(value);
                    setPage(1);
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <Autocomplete
                  fullWidth
                  options={customerOptions}
                  value={selectedCustomerOption}
                  onChange={(event, newValue) => {
                    setSelectedCustomer(
                      newValue?.id ? String(newValue.id) : ""
                    );
                    setPage(1);
                  }}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id ?? "") === String(value?.id ?? "")
                  }
                  clearOnBlur={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("reports.balancePayment_table_customer")}
                    />
                  )}
                />
                <Autocomplete
                  fullWidth
                  options={shopOptions}
                  value={selectedShopOption}
                  onChange={(event, newValue) => {
                    const nextId = newValue?.id ? String(newValue.id) : "";
                    setSelectedShop(nextId);
                    setShopSearchTerm(newValue?.name || "");
                    setSelectedCustomer("");
                    setPage(1);
                  }}
                  onInputChange={(event, value, reason) => {
                    if (reason === "input") setShopSearchTerm(value);
                    if (reason === "clear") setShopSearchTerm("");
                  }}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id ?? "") === String(value?.id ?? "")
                  }
                  loading={shopsLoading}
                  clearOnBlur={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("reports.shop", "Shop")}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedCustomer("");
                    setSelectedShop("");
                    setShopSearchTerm("");
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                >
                  {t("reports.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export", "Export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{ "aria-labelledby": "export-button" }}
                  >
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf", "Export PDF")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Divider />

      {/* Customer opening balance summary */}
      {Object.keys(totalOpeningBalance).length > 0 && (
        <Paper sx={{ p: 2, mb: 2, backgroundColor: "#f5f5f5" }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t("reports.customerOpeningBalance", "Customer opening balance")} (
            {t("reports.existingDues", "existing dues")})
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {Object.entries(totalOpeningBalance).map(([currency, amount]) => (
              <Typography key={currency} variant="body1">
                <strong>{amount.toFixed(2)}</strong> {currency}
              </Typography>
            ))}
          </Box>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table aria-label="balance payment table">
          <TableHead>
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography variant="h4">
                  {t("reports.balancePayment_tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("reports.balancePayment_table_id")}</TableCell>
              <TableCell>
                {t("reports.balancePayment_table_customer")}
              </TableCell>
              <TableCell>{t("reports.balancePayment_table_amount")}</TableCell>
              <TableCell>
                {t("reports.balancePayment_table_currency")}
              </TableCell>
              <TableCell>
                {t("reports.balancePayment_table_paymentDate")}
              </TableCell>
              <TableCell>{t("reports.balancePayment_table_notes")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayPayments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>{payment.id}</TableCell>
                <TableCell>{payment.customer_details?.name || "-"}</TableCell>
                <TableCell>{payment.amount ?? "0.00"}</TableCell>
                <TableCell>{payment.currency_details?.title || "-"}</TableCell>
                <TableCell>
                  {payment.payment_date
                    ? new Date(payment.payment_date).toLocaleString()
                    : "-"}
                </TableCell>
                <TableCell>{payment.notes || "-"}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
              <TableCell />
              <TableCell sx={{ fontWeight: "bold" }}>{t("Summary")}</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {totalAmount.toFixed(2)}
              </TableCell>
              <TableCell colSpan={3} />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center">
          <Typography>{t("reports.rowsPerPage")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": { "& fieldset": { border: "none" } },
            }}
          >
            <Select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={-1}>{t("reports.all")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {rowsPerPage !== -1 && (
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page}
            onChange={(e, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
          />
        )}
      </Grid>
    </>
  );
}
